import type { ReactNode } from "react";
import type { CompiledFormFamily } from "@/features/forms/shared/form-schema/family";
import type { PagerPage } from "@/features/forms/shared/record/page-status";
import type { RecordRow } from "@/features/forms/shared/record/record-values";
import type { AdviceFormSchema, AdviceLocale } from "@/shared/forms/schema-types";

const RECORD_FAMILY_KEYS = [
  "advice-individual",
  "account-opening-individual",
  "advice-corporation",
  "account-opening-corporation",
  "order-instruction",
  "endowment-insurance-individual",
  "endowment-insurance-corporation",
  "sign-document",
] as const;

export type RecordFamilyKey = (typeof RECORD_FAMILY_KEYS)[number];

export type SignDocumentFamilyKey = Extract<RecordFamilyKey, "sign-document">;

export type PanelFamilyKey = Exclude<RecordFamilyKey, SignDocumentFamilyKey>;

export type RecordSubjectKind = "individual" | "corporation";

export type RecordClientField = "individual_client_id" | "corporation_client_id";

export type RecordFamily = PanelFamily | SignDocumentRecordFamily;

export type PanelFamily =
  | AdviceFamily
  | AccountOpeningFamily
  | OrderInstructionFamily
  | EndowmentFamily;

export type AdviceFamily = AdviceTraits & RecordFormBinding;

export type AccountOpeningFamily = AccountOpeningTraits & RecordFormBinding;

type OrderInstructionFamily = OrderInstructionTraits & RecordFormBinding;

type EndowmentFamily = EndowmentTraits & RecordFormBinding;

export type SignDocumentRecordFamily = SignDocumentTraits & RecordFormBinding;

export type ClientFamily = AdviceFamily | AccountOpeningFamily;

export type SourcedFamily = OrderInstructionFamily | EndowmentFamily;

export type RecordFamilyTraits =
  | AdviceTraits
  | AccountOpeningTraits
  | OrderInstructionTraits
  | EndowmentTraits
  | SignDocumentTraits;

type AdviceTraits = RecordTraits<"advice"> & FixedSubject & ClientOrigin;

type AccountOpeningTraits = RecordTraits<"account_opening"> & FixedSubject & ClientOrigin;

type OrderInstructionTraits = RecordTraits<"order_instruction"> & PerRecordSubject & SourceOrigin;

type EndowmentTraits = RecordTraits<"endowment_insurance"> & FixedSubject & SourceOrigin;

type SignDocumentTraits = RecordTraits<"sign_document"> &
  PerRecordSubject &
  UnregisteredClientOrigin;

export type RecordFamilyKind =
  | "advice"
  | "account_opening"
  | "order_instruction"
  | "endowment_insurance"
  | "sign_document";

export interface RecordTraits<K extends RecordFamilyKind> {
  key: FamilyKeyOf<K>;
  kind: K;
  path: string;
  entity: string;
  signers: RecordSigners;
  signedByFirm: boolean;
  carriesPortfolio: boolean;
  offersDeclarationDraft: boolean;
  formTypeKey: string;
  createdStep?: string;
}

export type FamilyKeyOf<K extends RecordFamilyKind> = K extends "sign_document"
  ? SignDocumentFamilyKey
  : PanelFamilyKey;

export type RecordSigners =
  | { source: "client-contact" }
  | { source: "field"; role: "policyholder"; email: string; first: string; last: string }
  | {
      source: "table";
      role: "signatory";
      path: string;
      first: string;
      last: string;
      email: string;
    };

export interface FixedSubject {
  subject: RecordSubjectKind;
  clientField: RecordClientField;
}

interface PerRecordSubject {
  subject: "per-record";
}

export interface ClientOrigin {
  origin: { from: "client" };
  identificationNumberField: string;
  addressField: string;
}

interface UnregisteredClientOrigin {
  origin: { from: "client" };
}

interface SourceOrigin {
  origin: RecordSourceOrigin;
}

export interface RecordSourceOrigin {
  from: "source";
  sources: readonly RecordSourceLink[];
}

export interface RecordSourceLink {
  family: RecordFamilyKey;
  idField: string;
  available: string;
  quantBound?: boolean;
}

export interface RecordSubjectLink {
  kind: RecordSubjectKind;
  clientLinkId: string;
}

export interface RecordFormBinding {
  form: CompiledFormFamily;
  pages: (locale: AdviceLocale, schema: AdviceFormSchema) => PagerPage[];
  facts?: RecordFactsBlock;
  documents?: RecordFieldDocuments;
  createSeed?: (family: RecordFamily, clientLinkId: string) => Promise<Record<string, unknown>>;
}

export type RecordFieldDocuments = Readonly<Record<string, FieldDocument | null>>;

export type FieldDocument = { url: string } | { binaryFileId: string };

export interface RecordFactsBlock {
  section: string;
  placement: "before" | "within";
  render: (record: RecordRow) => ReactNode;
}

export const CLIENT_FIELD: Readonly<Record<RecordSubjectKind, RecordClientField>> = {
  individual: "individual_client_id",
  corporation: "corporation_client_id",
};

export function subjectOf(family: RecordFamily, record: object): RecordSubjectLink | null {
  const row = record as Readonly<Record<string, unknown>>;
  for (const kind of subjectKindsOf(family)) {
    const clientLinkId = row[CLIENT_FIELD[kind]];
    if (typeof clientLinkId === "string" && clientLinkId !== "") {
      return { kind, clientLinkId };
    }
  }
  return null;
}

export function subjectKindsOf(family: {
  subject: RecordSubjectKind | "per-record";
}): readonly RecordSubjectKind[] {
  return family.subject === "per-record" ? SUBJECT_KINDS : [family.subject];
}

export function isSourcedFamily(family: RecordFamily): family is SourcedFamily {
  return family.origin.from === "source";
}

export function isClientFamily(family: RecordFamily): family is ClientFamily {
  return family.kind === "advice" || family.kind === "account_opening";
}

export function hasClientRegister(family: RecordFamily): family is RecordFamily & ClientOrigin {
  return "identificationNumberField" in family;
}

export function isRecordFamilyKey(value: unknown): value is RecordFamilyKey {
  return typeof value === "string" && (RECORD_FAMILY_KEYS as readonly string[]).includes(value);
}

const SUBJECT_KINDS: readonly RecordSubjectKind[] = ["individual", "corporation"];
